import * as React from "react";
import { TenantConfig } from "../types/tenant";
import { TenantContext, TenantContextType } from "../contexts/TenantContext";
import { TenantLoadingStates } from "../components/loading/TenantLoadingStates";
import "../styles/neutral-styles.css";
import { logger } from "../utils/logger";
import { initializeTenantAnalytics } from "../analytics-init";
import { useTenantConfig } from "../hooks/tenant/useTenantConfig";

interface TenantProviderProps {
  children: React.ReactNode;
  testHostname?: string;
}

export const OptimizedTenantProvider: React.FC<TenantProviderProps> = ({
  children,
  testHostname,
}) => {
  const [isInitialSetupComplete, setIsInitialSetupComplete] =
    React.useState(false);

  // Use the React Query hook to fetch tenant config
  const {
    data: config,
    isLoading,
    error,
    isError,
  } = useTenantConfig({
    hostname: testHostname,
  });

  // Apply tenant styles and initialize analytics when config changes
  React.useEffect(() => {
    if (config && typeof window !== "undefined") {
      // Set global tenant info for analytics
      (window as any).__TENANT_ID__ = config.id;
      (window as any).__ORG_ID__ = config.orgData?.id;

      // Initialize analytics
      initializeTenantAnalytics(config)
        .then(() => {
          // Apply styles
          applyTenantStyles(config.branding);
          setIsInitialSetupComplete(true);
        })
        .catch((error) => {
          logger.error("Failed to initialize analytics", { error });
          // Still apply styles even if analytics fails
          applyTenantStyles(config.branding);
          setIsInitialSetupComplete(true);
        });
    }
  }, [config]);

  // Create the context value
  const contextValue = React.useMemo<TenantContextType>(
    () => ({
      config: config || null,
      isLoading: isLoading && !isInitialSetupComplete,
      error: isError ? (error as Error) : null,
    }),
    [config, isLoading, isError, error, isInitialSetupComplete]
  );

  return (
    <TenantContext.Provider value={contextValue}>
      {isLoading && !isInitialSetupComplete ? (
        <div className="fixed inset-0 z-[9999] bg-white">
          <TenantLoadingStates
            state={isError ? "error" : "immediate"}
            retryCount={0}
            error={isError ? (error as Error) : null}
            onRetry={() => window.location.reload()}
          />
        </div>
      ) : (
        children
      )}
    </TenantContext.Provider>
  );
};

// Helper function to apply tenant styles
const applyTenantStyles = (branding: TenantConfig["branding"]) => {
  if (typeof window === "undefined" || !branding) return;

  const root = document.documentElement;
  if (branding.colors) {
    // Set CSS variables to match tenant config color names
    Object.entries(branding.colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, String(value));
    });
  }
  if (branding.fonts) {
    root.style.setProperty("--font-primary", branding.fonts.primary);
    root.style.setProperty("--font-secondary", branding.fonts.secondary);
  }

  // Force a re-render of styles
  if (branding.colors) {
    document.body.style.backgroundColor = branding.colors.background || "";
    document.body.style.color = branding.colors.text || "";
  }
};
